import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, BarChart3, CheckCircle2, Loader2, Save, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TiptapEditor } from '@/components/content/tiptap-editor';
import { MediaUploader } from '@/components/content/media-uploader';
import { VisibilityPicker } from '@/components/content/visibility-picker';
import { VideoEmbedInput } from '@/components/content/video-embed-input';
import { SeedLinkPicker } from '@/components/content/seed-link-picker';
import { ModerationActionsBar } from '@/components/content/moderation-actions-bar';
import { ModerationLogPanel } from '@/components/content/moderation-log-panel';
import { useAuthContext } from '@/contexts/auth-context';
import {
  useContentDetail,
  useCreateContent,
  useDeleteContent,
  useSetSeedLinks,
  useSubmitContent,
  useUpdateContent,
} from '@/hooks/use-content';
import { toToastMessage } from '@/lib/api-error';
import {
  ContentStatus,
  ContentSubscriptionTier,
  ContentType,
  ContentVisibility,
  contentStatusLabels,
  contentTierLabels,
  contentTypeLabels,
  emptyTiptapDoc,
  type ContentInput,
  type ContentItem,
  type ContentMediaAsset,
  type ContentSeedLink,
  type ContentSubscriptionTier as ContentSubscriptionTierType,
  type ContentType as ContentTypeT,
  type ContentVisibility as ContentVisibilityT,
  type TiptapDoc,
  type VisibilityConfig,
} from '@/types/content';

// SPEC-021: ContentEditorPage — one form for create + edit. When :id is
// missing it creates a new item on first save; subsequent saves call
// update. Media upload is gated until the draft exists because presigned
// keys are scoped under content/{id}/... .

interface FormState {
  title: string;
  summary: string;
  contentType: ContentTypeT;
  language: string;
  category: string;
  tier: ContentSubscriptionTierType;
  visibility: ContentVisibilityT;
  visibilityConfig: VisibilityConfig | null;
  body: TiptapDoc;
  embedURL: string;
}

const emptyForm: FormState = {
  title: '',
  summary: '',
  contentType: ContentType.Article,
  language: 'en',
  category: '',
  tier: ContentSubscriptionTier.Free,
  visibility: ContentVisibility.TeacherStudents,
  visibilityConfig: null,
  body: emptyTiptapDoc(),
  embedURL: '',
};

export default function ContentEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';

  const detail = useContentDetail(isNew ? undefined : id);
  const create = useCreateContent();
  const update = useUpdateContent();
  const submit = useSubmitContent();
  const del = useDeleteContent();
  const setSeedLinksMut = useSetSeedLinks();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [seedLinks, setSeedLinks] = useState<
    Array<Pick<ContentSeedLink, 'seed_id' | 'link_type'>>
  >([]);
  const [localAssets, setLocalAssets] = useState<ContentMediaAsset[]>([]);

  // Sync form state whenever the loaded item changes.
  useEffect(() => {
    if (!detail.data) return;
    const item = detail.data;
    setForm({
      title: item.title,
      summary: item.summary ?? '',
      contentType: item.content_type,
      language: item.language,
      category: item.category ?? '',
      tier: item.min_subscription_tier,
      visibility: item.visibility,
      visibilityConfig: item.visibility_config ?? null,
      body: item.body ?? emptyTiptapDoc(),
      embedURL: item.embed_url ?? '',
    });
    setSeedLinks(
      (item.seed_links ?? []).map((l) => ({ seed_id: l.seed_id, link_type: l.link_type })),
    );
    setLocalAssets(item.media ?? []);
  }, [detail.data]);

  const buildPayload = (): ContentInput => ({
    title: form.title,
    summary: form.summary || null,
    content_type: form.contentType,
    language: form.language,
    category: form.category || null,
    min_subscription_tier: form.tier,
    visibility: form.visibility,
    visibility_config: form.visibilityConfig,
    body: form.body,
    embed_url: form.embedURL || null,
  });

  const validateForSave = (): string | null => {
    if (!form.title.trim()) return 'Title is required';
    if (form.body.content.length === 0) return 'Body cannot be empty';
    return null;
  };

  const handleSaveDraft = async () => {
    const err = validateForSave();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      if (isNew) {
        const created = await create.mutateAsync(buildPayload());
        if (seedLinks.length > 0) {
          await setSeedLinksMut.mutateAsync({ id: created.id, links: seedLinks });
        }
        toast.success('Draft created');
        navigate(`/content/${created.id}`, { replace: true });
      } else {
        await update.mutateAsync({ id: id as string, input: buildPayload() });
        await setSeedLinksMut.mutateAsync({ id: id as string, links: seedLinks });
        toast.success('Draft saved');
      }
    } catch (e) {
      toast.error(toToastMessage(e, 'Save failed'));
    }
  };

  const handleSubmitForReview = async () => {
    if (isNew) {
      toast.error('Save a draft first before submitting for review.');
      return;
    }
    const err = validateForSave();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      // Always persist latest edits before submitting.
      await update.mutateAsync({ id: id as string, input: buildPayload() });
      await setSeedLinksMut.mutateAsync({ id: id as string, links: seedLinks });
      await submit.mutateAsync(id as string);
      toast.success('Submitted for review');
    } catch (e) {
      toast.error(toToastMessage(e, 'Submit failed'));
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm('Delete this content item? It can be restored from Trash.')) return;
    try {
      await del.mutateAsync(id as string);
      toast.success('Deleted');
      navigate('/content', { replace: true });
    } catch (e) {
      toast.error(toToastMessage(e, 'Delete failed'));
    }
  };

  const loaded = isNew || detail.isSuccess;
  const pending = create.isPending || update.isPending || submit.isPending || del.isPending;
  const current: ContentItem | undefined = detail.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-xl font-semibold">
          {isNew ? 'New content' : form.title || 'Untitled content'}
        </h1>
        {current && <StatusIndicator item={current} />}
        <div className="ml-auto flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" onClick={handleDelete} disabled={pending}>
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={pending}>
            {pending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Save draft
          </Button>
          <Button size="sm" onClick={handleSubmitForReview} disabled={isNew || pending}>
            <Send className="mr-1 h-4 w-4" /> Submit for review
          </Button>
        </div>
      </div>

      {current?.rejection_reason && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-4 text-sm">
            <p className="font-medium text-destructive">Rejection reason</p>
            <p className="mt-1">{current.rejection_reason}</p>
          </CardContent>
        </Card>
      )}

      {isAdmin && current && <ModerationActionsBar item={current} />}

      {!loaded ? (
        <div className="flex items-center justify-center gap-2 p-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Morning stillness"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary">Summary</Label>
                  <textarea
                    id="summary"
                    rows={2}
                    value={form.summary}
                    onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="One-sentence teaser shown on the feed card"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Content type</Label>
                    <Select
                      value={form.contentType}
                      onValueChange={(v) =>
                        setForm((p) => ({ ...p, contentType: v as ContentTypeT }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(contentTypeLabels) as ContentTypeT[]).map((k) => (
                          <SelectItem key={k} value={k}>
                            {contentTypeLabels[k]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={form.category}
                      onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                      placeholder="optional"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Body</CardTitle>
              </CardHeader>
              <CardContent>
                <TiptapEditor
                  value={form.body}
                  onChange={(doc) => setForm((p) => ({ ...p, body: doc }))}
                />
              </CardContent>
            </Card>

            {(form.contentType === ContentType.Video ||
              form.contentType === ContentType.Mixed) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Video embed</CardTitle>
                </CardHeader>
                <CardContent>
                  <VideoEmbedInput
                    value={form.embedURL}
                    onChange={(url) => setForm((p) => ({ ...p, embedURL: url ?? '' }))}
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Media assets</CardTitle>
              </CardHeader>
              <CardContent>
                {isNew ? (
                  <p className="text-sm text-muted-foreground">
                    Save the draft first to upload images and audio.
                  </p>
                ) : (
                  <MediaUploader
                    contentId={id as string}
                    assets={localAssets}
                    onUploaded={(a) => setLocalAssets((prev) => [...prev, a])}
                    onDeleted={(assetId) =>
                      setLocalAssets((prev) => prev.filter((x) => x.id !== assetId))
                    }
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Related seeds</CardTitle>
              </CardHeader>
              <CardContent>
                <SeedLinkPicker links={seedLinks} onChange={setSeedLinks} />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Audience</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <VisibilityPicker
                  visibility={form.visibility}
                  config={form.visibilityConfig}
                  onChange={(visibility, config) =>
                    setForm((p) => ({
                      ...p,
                      visibility,
                      visibilityConfig: config,
                    }))
                  }
                />
                <div className="space-y-2">
                  <Label>Minimum subscription tier</Label>
                  <Select
                    value={form.tier}
                    onValueChange={(v) =>
                      setForm((p) => ({ ...p, tier: v as ContentSubscriptionTierType }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(contentTierLabels) as ContentSubscriptionTierType[]).map(
                        (k) => (
                          <SelectItem key={k} value={k}>
                            {contentTierLabels[k]}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {current && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Detail label="Status" value={contentStatusLabels[current.status]} />
                  <Detail
                    label="Estimated"
                    value={
                      current.estimated_minutes
                        ? `${current.estimated_minutes} min`
                        : 'unknown'
                    }
                  />
                  <Detail label="Slug" value={<code className="text-xs">{current.slug}</code>} />
                  {current.published_at && (
                    <Detail
                      label="Published"
                      value={new Date(current.published_at).toLocaleString()}
                    />
                  )}
                  <Detail
                    label="Updated"
                    value={new Date(current.updated_at).toLocaleString()}
                  />
                  {current.published_at && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => navigate(`/content/${current.id}/metrics`)}
                    >
                      <BarChart3 className="mr-1 h-4 w-4" />
                      View metrics
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {isAdmin && current && <ModerationLogPanel contentId={current.id} />}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right">{value}</span>
    </div>
  );
}

function StatusIndicator({ item }: { item: ContentItem }) {
  if (item.status === ContentStatus.Published) {
    return (
      <Badge variant="default" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Published
      </Badge>
    );
  }
  return <Badge variant="outline">{contentStatusLabels[item.status]}</Badge>;
}
